import React, { ReactNode } from "react";
import { attendanceStatusColor, IEventTileProps } from "./EventTile";
import { Box, Button, IconButton, Popover, Stack, useTheme } from "@mui/material";
import { useUpdateEventAttendance } from "../../../services/react-query/events";
import { DateTime } from "luxon";
import { Check, Close, Login, Logout, MoreHoriz, TaskAlt } from "@mui/icons-material";
import { AttendanceCommand, AttendanceStatus } from "../../../types/Event";
import { TaskStatus } from "../../../types/Task";
import { taskBaseColor } from "../../../utility/task";

export function AttendanceActions({ event, attendance }: IEventTileProps) {
  const theme = useTheme();
  const { mutate: updateAttendance } = useUpdateEventAttendance({ uuid: event.uuid });
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const now = DateTime.now();
  const eventStart = DateTime.fromISO(event.start_at);
  const attendanceCutoff = eventStart.minus({ hours: 12 });
  const checkInCutoff = eventStart.minus({ hours: 1 });
  const eventEnd = DateTime.fromISO(event.end_at);

  if (!attendance) return null;

  let mainButton: ReactNode = null;
  let secondaryButton: ReactNode = null;
  if (now < attendanceCutoff) {
    if (attendance.status == AttendanceStatus.PENDING) {
      mainButton = (
        <Button
          startIcon={<Check />}
          size="small"
          onClick={() => updateAttendance(AttendanceCommand.ACCEPT)}
          sx={{ color: attendanceStatusColor(attendance.status, theme) }}
          title="Accept"
        >
          Accept
        </Button>
      );
      secondaryButton = (
        <Button
          startIcon={<Close />}
          size="small"
          onClick={() => updateAttendance(AttendanceCommand.DECLINE)}
          sx={{ color: attendanceStatusColor(AttendanceStatus.DECLINED, theme) }}
          title="Accept"
        >
          Reject
        </Button>
      );
    } else if (attendance.status == AttendanceStatus.ACCEPTED) {
      mainButton = (
        <Button
          disabled
          startIcon={<Check />}
          size="small"
          sx={{ '&.Mui-disabled': { color: attendanceStatusColor(attendance.status, theme) } }}
        >
          Accepted
        </Button>
      );
      secondaryButton = (
        <Button
          startIcon={<Close />}
          size="small"
          onClick={() => updateAttendance(AttendanceCommand.DECLINE)}
          sx={{ color: attendanceStatusColor(AttendanceStatus.DECLINED, theme) }}
          title="Accept"
        >
          Reject
        </Button>
      );
    } else if (attendance.status == AttendanceStatus.DECLINED) {
      mainButton = (
        <Button
          disabled
          startIcon={<Close />}
          size="small"
          sx={{ '&.Mui-disabled': { color: attendanceStatusColor(attendance.status, theme) } }}
        >
          Rejected
        </Button>
      );
      secondaryButton = (
        <Button
          startIcon={<Check />}
          size="small"
          onClick={() => updateAttendance(AttendanceCommand.ACCEPT)}
          sx={{ color: attendanceStatusColor(AttendanceStatus.ACCEPTED, theme) }}
          title="Accept"
        >
          Accept
        </Button>
      );
    }
  } else if (now > checkInCutoff && now < eventEnd) {
    if (attendance.checked_out_at) {
      mainButton = (
        <Button
          disabled
          startIcon={<TaskAlt />}
          size="small"
          sx={{ '&.Mui-disabled': { color: theme.palette.success.main } }}
        >
          Checked Out
        </Button>
      );
      secondaryButton = (
        <Button
          startIcon={<Login />}
          size="small"
          onClick={() => updateAttendance(AttendanceCommand.CHECK_IN)}
          sx={{ color: theme.palette.primary.main }}
        >
          Check In
        </Button>
      );
    } else if (attendance.checked_in_at) {
      mainButton = (
        <Button
          startIcon={<Logout />}
          size="small"
          onClick={() => updateAttendance(AttendanceCommand.CHECK_OUT)}
          sx={{ color: taskBaseColor(TaskStatus.IN_PROGRESS) }}
        >
          Check Out
        </Button>
      );
    } else {
      mainButton = (
        <Button
          startIcon={<Login />}
          size="small"
          onClick={() => updateAttendance(AttendanceCommand.CHECK_IN)}
          sx={{ color: theme.palette.primary.main }}
        >
          Check In
        </Button>
      );
    }
  } else {
    return null;
  }



  const menu = null;

  return (
    <Stack spacing={1} justifyContent="center" alignItems="end">
      {mainButton}
      {secondaryButton && (
        <>
          <IconButton onClick={(event) => setAnchorEl(event.currentTarget)} size="small" sx={{ width: 'fit-content' }}>
            <MoreHoriz />
          </IconButton>
          <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
            <Box margin={0.5}>
              {secondaryButton}
            </Box>
          </Popover>
        </>
      )}
    </Stack>
  );
}
